import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { SnapshotRow } from "./types";

export interface SnapshotsViewProps {
    revision: number;
    onRestored: () => void;
}

const SnapshotsView = ({ revision, onRestored }: SnapshotsViewProps) => {
    const { t } = useTranslation();
    const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [reason, setReason] = useState("");
    const [taking, setTaking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmId, setConfirmId] = useState<string | number | null>(null);
    const [restoring, setRestoring] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/access-control/snapshots");
            if (!res.ok) { setError(t("server_error")); return; }
            const body: { snapshots: SnapshotRow[] } = await res.json();
            setSnapshots(body.snapshots);
        } catch {
            setError(t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const takeSnapshot = async () => {
        setTaking(true);
        setError(null);
        try {
            const res = await apiFetch("/access-control/snapshots", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: reason.trim() || null }),
            });
            if (!res.ok) { setError(t("server_error")); return; }
            setReason("");
            await load();
        } catch {
            setError(t("server_error"));
        } finally {
            setTaking(false);
        }
    };

    const restore = async (id: string | number) => {
        setRestoring(true);
        setError(null);
        try {
            const res = await apiFetch(`/access-control/snapshots/${id}/restore`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ base_revision: revision }),
            });
            if (res.status === 409) {
                setError(t("ac_revision_conflict"));
                return;
            }
            if (!res.ok) { setError(t("server_error")); return; }
            setConfirmId(null);
            onRestored();
            await load();
        } catch {
            setError(t("server_error"));
        } finally {
            setRestoring(false);
        }
    };

    return (
        <div className="ac-snapshots">
            {error && <p className="ac-hint ac-hint--error">{error}</p>}

            <div className="ac-snapshots-take">
                <input
                    type="text"
                    placeholder={t("ac_snapshot_reason_placeholder")}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    maxLength={200}
                />
                <button type="button" className="btn btn-primary btn-sm" onClick={takeSnapshot} disabled={taking}>
                    {taking ? t("saving") : t("ac_snapshot_take")}
                </button>
            </div>

            {loading && <div className="ac-users-loading">{t("loading")}</div>}

            <div className="ac-matrix-scroll">
                <table className="ac-matrix-table">
                    <thead>
                        <tr>
                            <th>{t("ac_snapshot_when")}</th>
                            <th>{t("ac_audit_who")}</th>
                            <th>{t("ac_snapshot_reason")}</th>
                            <th>{t("ac_snapshot_revision")}</th>
                            <th>{t("ac_snapshot_rule_count")}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {snapshots.map(s => (
                            <tr key={s.id}>
                                <td>{new Date(s.taken_at).toLocaleString()}</td>
                                <td>{s.taken_by}</td>
                                <td>{s.reason ?? "—"}</td>
                                <td>{s.revision}</td>
                                <td>{s.rule_count}</td>
                                <td>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmId(s.id)}>
                                        {t("ac_snapshot_restore")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && snapshots.length === 0 && (
                            <tr><td colSpan={6} className="ac-hint">{t("ac_snapshot_empty")}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {confirmId !== null && (
                <div className="modal-overlay" onClick={() => !restoring && setConfirmId(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("ac_snapshot_restore_title")}</h2>
                        </div>
                        <div className="modal-card-body">
                            <p>{t("ac_snapshot_restore_body")}</p>
                            <div className="modal-confirm-buttons">
                                <button type="button" className="btn btn-ghost" onClick={() => setConfirmId(null)} disabled={restoring}>
                                    {t("cancel")}
                                </button>
                                <button type="button" className="btn btn-primary" onClick={() => restore(confirmId)} disabled={restoring}>
                                    {restoring ? t("submitting") : t("ac_snapshot_restore")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SnapshotsView;
